/**
 * PriorContractAutocomplete Component
 * Autocomplete picker for selecting a prior contract when entering rolled funds.
 * Shows remaining unallocated balance for each candidate contract.
 * Converts balance to the new contract's currency using last loaded FX rate.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { apiGet, extractApiData } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import logger from '../../utils/logger';
import PriorContractFormDialog from './PriorContractFormDialog';

// Shared exceed computation so the field validator (parent form) and this
// autocomplete's warning agree exactly. Mirrors the DB guard
// fn_contract_check_rolled_remaining (T3-030): compares the rolled amount against
// the source's remaining balance, converting cross-currency via the live FX rate.
// Returns { wouldExceed, remainingConverted, noFx }:
//   - remainingConverted: the source's remaining expressed in the NEW contract's ccy
//   - wouldExceed: rolled > remainingConverted + 0.005 tolerance
//   - noFx: cross-currency but no FX rate available -> cannot validate -> block (fail closed)
// T3-033 SLICE-ONLY: for a source that has more than one investor (a non-empty
// per_investor_remaining), the amount this contract's investor may roll is THAT investor's
// SLICE (entry.remaining), NEVER the whole-contract remaining_balance. Return a shallow copy of
// the option with remaining_balance swapped to the investor's slice so every downstream reader
// (computeRolledExceed / getEffectiveRemaining / the dropdown + field display) caps and shows the
// slice. For a genuinely single-investor source (empty per_investor_remaining) the option is
// returned unchanged and the overall remaining_balance stands. Enforces never-cross-investor and
// mirrors sp_contract_set_rollover_sources passing the investor into the DB guard.
// Returns { option, noSlice }: noSlice=true when the source has a per-investor breakdown but NONE
// for this investor (blocked — investor has no rollable balance on this source).
export function sliceOptionForInvestor(option, investorId) {
  if (!option || !investorId) return { option, noSlice: false };
  const pir = Array.isArray(option.per_investor_remaining) ? option.per_investor_remaining : [];
  if (pir.length === 0) return { option, noSlice: false }; // single-investor source: overall pool is the rule
  const entry = pir.find((e) => parseInt(e.investor_id) === parseInt(investorId));
  if (!entry) return { option, noSlice: true };
  return { option: { ...option, remaining_balance: entry.remaining }, noSlice: false };
}

// NOTE (T3-096): do NOT add an edit-self "credit back this row's saved amount" here. The rollable
// list is fetched with p_exclude_contract_id = this contract, so remaining_balance ALREADY excludes
// this contract's own allocation — crediting again double-counts and lets a genuine over-allocation
// through (measured: a 50 001 roll against a 50 000 slice stopped being flagged).
export function computeRolledExceed({ selectedOption, rolledFundsAmount, currencyCode, fxRate }) {
  if (!selectedOption) return { wouldExceed: false, remainingConverted: null, noFx: false };
  const serverRemaining = parseFloat(selectedOption.remaining_balance) || 0;
  const rolled = parseFloat(rolledFundsAmount) || 0;
  // Only treat as cross-currency when BOTH currencies are known AND actually differ. When
  // the source's currency_code is unknown (e.g. a fallback stub or not-yet-resolved option),
  // do NOT assume cross-currency and demand an FX rate — treat as same-currency and let the
  // DB be the authority. (Assuming cross-currency on an unknown ccy produced the spurious
  // "No FX rate for undefined/SZL" error.)
  const srcCcy = selectedOption.currency_code;
  const isCross = !!currencyCode && !!srcCcy && srcCcy !== currencyCode;
  if (isCross && !fxRate) {
    // Cross-currency without a rate: the amount can't be validated -> fail closed.
    return { wouldExceed: false, remainingConverted: null, noFx: true };
  }
  const remainingConverted = isCross ? serverRemaining * fxRate : serverRemaining;
  return { wouldExceed: rolled > remainingConverted + 0.005, remainingConverted, noFx: false };
}

// Search across reference, external reference, and investor name
const filterOptions = (options, { inputValue }) => {
  const search = inputValue.toLowerCase();
  if (!search) return options;
  return options.filter((opt) =>
    (opt.reference || '').toLowerCase().includes(search) ||
    (opt.user_reference || '').toLowerCase().includes(search) ||
    (opt.investor_name || '').toLowerCase().includes(search)
  );
};

function PriorContractAutocomplete({
  value,
  onChange,
  excludeId,
  disabled = false,
  error,
  helperText,
  rolledFundsAmount = 0,
  currencyCode = null,
  depositDate = null,
  investorId = null,
  investorName = null,
  // Fallback display when the linked contract isn't in the fetched options
  // (e.g. fully allocated and dropped off the rollable list). Form state
  // still has the link; this lets us show it.
  fallbackReference = null,
  fallbackUserReference = null,
  // Callback(rate|null, { pair, selectedOption }) fired whenever the live FX rate
  // for the prior->new currency pair resolves (or clears). Lets the parent form's
  // rolled-funds validator use the SAME rate this component displays, so the field
  // validator and the autocomplete warning never disagree.
  onFxRate = null,
  // T3-032: contract ids already chosen in OTHER rollover-source rows — hidden from this
  // row's dropdown so the same prior contract can't be picked twice on one contract.
  excludeIds = null,
  // T3-032: when true, render NO helper text under the field — the parent
  // (RolloverSourcesSection) shows a single full-width "Remaining after allocation" / error
  // line beneath the 50/50 row instead of the cramped per-field copy.
  suppressHelperText = false,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fxRate, setFxRate] = useState(null);
  const [fxPair, setFxPair] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRollable = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (excludeId) params.set('exclude_id', excludeId);
        if (depositDate) params.set('deposit_date', depositDate);
        if (investorId) params.set('investor_id', investorId);
        const qs = params.toString();
        const url = '/contracts/rollable-list' + (qs ? `?${qs}` : '');
        const response = await apiGet(url);
        const data = extractApiData(response);
        if (!cancelled) setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        logger.error('Error fetching rollable contracts', { message: err.message });
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRollable();
    return () => { cancelled = true; };
  }, [excludeId, depositDate, investorId]);

  // Resolve display option. Prefer the rich option from the fetched list;
  // fall back to a minimal stub built from form-state references if the
  // linked contract isn't (or no longer is) in the rollable list. Without
  // this fallback the Autocomplete shows empty even though `value` is set,
  // making the user think the link was lost on Back-navigation.
  // Memoised so the FX/reporting effects don't re-fire on every render (the fallback
  // stub is a fresh object literal each render otherwise).
  const selectedOption = useMemo(() => {
    const fetchedOption = options.find((opt) => opt.id === value) || null;
    return fetchedOption
      || (value && (fallbackReference || fallbackUserReference)
        ? { id: value, reference: fallbackReference || '', user_reference: fallbackUserReference || '' }
        : null);
  }, [options, value, fallbackReference, fallbackUserReference]);

  // Fetch FX rate when prior contract currency differs from new contract currency
  const fetchFxRate = useCallback(async (priorCcy, newCcy) => {
    if (!priorCcy || !newCcy || priorCcy === newCcy) {
      setFxRate(null);
      setFxPair(null);
      return;
    }
    try {
      // /exchange-rates/current/:pair returns { success, rate: <row> } where
      // row.rate is the numeric rate. Read the row directly — extractApiData
      // looks for .data which this endpoint doesn't use.
      const response = await apiGet(`/exchange-rates/current/${priorCcy}/${newCcy}`);
      const rateValue = response?.rate?.rate;
      if (rateValue !== undefined && rateValue !== null) {
        const num = parseFloat(rateValue);
        if (Number.isFinite(num)) {
          setFxRate(num);
          setFxPair(`${priorCcy}/${newCcy}`);
          return;
        }
      }
      setFxRate(null);
      setFxPair(null);
    } catch (err) {
      logger.error('Error fetching FX rate', { message: err.message });
      setFxRate(null);
      setFxPair(null);
    }
  }, []);

  // Re-fetch FX rate when selection or currency changes
  useEffect(() => {
    if (selectedOption && currencyCode && selectedOption.currency_code !== currencyCode) {
      fetchFxRate(selectedOption.currency_code, currencyCode);
    } else {
      setFxRate(null);
      setFxPair(null);
    }
  }, [selectedOption, currencyCode, fetchFxRate]);

  // Report the resolved rate up so the parent's rolled-funds validator uses the SAME
  // rate this component displays (single source of the rate). The callback is held in a
  // ref and is NOT an effect dependency: parents pass a fresh inline arrow each render,
  // so depending on it would loop (effect -> parent setState -> re-render -> new arrow ->
  // effect ...). We fire only when the VALUE actually changes (rate/pair/selected id).
  const onFxRateRef = useRef(onFxRate);
  useEffect(() => { onFxRateRef.current = onFxRate; }, [onFxRate]);
  // "Has-balance" signal: flips once when selectedOption upgrades from a balance-less stub to the
  // rich fetched option, so the reporting effect below re-fires even at the SAME id / same currency.
  const selectedPirLen = Array.isArray(selectedOption?.per_investor_remaining)
    ? selectedOption.per_investor_remaining.length
    : -1;
  useEffect(() => {
    if (onFxRateRef.current) onFxRateRef.current(fxRate, { pair: fxPair, selectedOption });
    // Re-fire when the option gains BALANCE data, not just when its id changes. On edit-rehydrate
    // selectedOption starts as a balance-less STUB {id,reference} and later upgrades to the rich
    // fetched option (with remaining_balance / per_investor_remaining) at the SAME id; for a
    // same-currency row fxRate/fxPair stay null too, so keying only on `selectedOption?.id` never
    // re-fired and the parent (RolloverSourcesSection) never upgraded the row's _option →
    // computeRolledExceed read remaining 0 → false "Exceeds remaining (0.00)". These signals flip
    // once (stub→rich) then stay stable, so no churn; the callback is held in a ref (not a loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxRate, fxPair, selectedOption?.id, selectedOption?.remaining_balance, selectedPirLen]);

  const handleChange = (_, newValue) => {
    if (newValue && newValue._isCreateHistorical) {
      setCreateDialogOpen(true);
      return;
    }
    onChange(newValue?.id || null, newValue);
  };

  const handleHistoricalSaved = (contractStub) => {
    // Add the new stub to options and select it
    setOptions(prev => [...prev, contractStub]);
    onChange(contractStub.id, contractStub);
    setCreateDialogOpen(false);
  };

  // Amounts here are always real computed numbers; keep the '0.00' fallback for the
  // NaN case (formatNumber returns '-') so rollover/remaining displays never read blank.
  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    return formatNumber(num, 2);
  };

  const getEffectiveRemaining = (option) => {
    if (!option) return 0;
    return parseFloat(option.remaining_balance) || 0;
  };

  const isCrossCurrency = selectedOption && currencyCode && selectedOption.currency_code !== currencyCode;

  return (
    <>
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      loading={loading}
      disabled={disabled}
      filterOptions={(opts, params) => {
        let filtered = filterOptions(opts, params);
        // T3-032: hide sources already chosen in other rows (but never hide THIS row's own
        // current selection, so it still displays).
        if (Array.isArray(excludeIds) && excludeIds.length > 0) {
          const hide = new Set(excludeIds.filter((x) => x !== value));
          filtered = filtered.filter((o) => !hide.has(o.id));
        }
        filtered.push({ _isCreateHistorical: true, id: '__create__' });
        return filtered;
      }}
      getOptionLabel={(option) => {
        if (option._isCreateHistorical) return '';
        // Lead with the system reference (unique) + the external reference. Append the maturity
        // date so two prior contracts that share the same external reference (e.g. two "SML805"
        // of different amount/maturity) are distinguishable in the COLLAPSED field, not just in
        // the expanded option row. Same date idiom as renderOption below.
        const base = option.reference + (option.user_reference ? ` (${option.user_reference})` : '');
        const mat = option.maturity_date
          ? new Date(option.maturity_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';
        return mat ? `${base} · mat ${mat}` : base;
      }}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      renderOption={(props, option) => {
        if (option._isCreateHistorical) {
          const { key, ...restProps } = props;
          return (
            <Box
              component="li"
              key="create-historical"
              {...restProps}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="primary" fontWeight={600}>
                Enter Historical Contract
              </Typography>
            </Box>
          );
        }
        // T3-033 SLICE-ONLY: show THIS investor's slice of a multi-investor source (never the
        // whole pool). sliceOpt.remaining_balance is entry.remaining when a breakdown exists.
        const { option: sliceOpt } = sliceOptionForInvestor(option, investorId);
        const remaining = getEffectiveRemaining(sliceOpt);
        // "Already rolled" against this investor's slice when the breakdown exists, else overall.
        const pirEntry = Array.isArray(option.per_investor_remaining) && investorId
          ? option.per_investor_remaining.find((e) => parseInt(e.investor_id) === parseInt(investorId))
          : null;
        const allocated = pirEntry
          ? (parseFloat(pirEntry.allocated) || 0)
          : (parseFloat(option.allocated_rolled_amount) || 0);
        // For the CURRENTLY SELECTED option, show the remaining AFTER this row's allocation
        // (server remaining, which already nets funds rolled by other contracts, minus what
        // this row takes). computeRolledExceed handles the same cross-currency conversion as
        // the field helper so the two never disagree. Non-selected options show raw remaining.
        const isSelectedRow = option.id === value;
        const rowAmt = parseFloat(rolledFundsAmount) || 0;
        const afterExceed = isSelectedRow && rowAmt > 0
          ? computeRolledExceed({ selectedOption: sliceOpt, rolledFundsAmount: rowAmt, currencyCode, fxRate })
          : null;
        const showAfter = afterExceed && afterExceed.remainingConverted !== null;
        const displayRemaining = showAfter ? (afterExceed.remainingConverted - rowAmt) : remaining;
        const remainingCcy = showAfter ? (currencyCode || option.currency_code) : option.currency_code;
        const isOverAllocated = displayRemaining < 0;
        const maturityStr = option.maturity_date
          ? new Date(option.maturity_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';
        return (
          <li {...props} key={option.id}>
            <Box sx={{ width: '100%', py: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  {option.user_reference || option.reference}
                  {option.user_reference && (
                    <Typography component="span" variant="body2" color="textSecondary">
                      {' '}({option.reference})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {option.currency_code} {formatAmount(option.capital_amount)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  {option.investor_name} · {option.status}{maturityStr ? ` · Matures ${maturityStr}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.25 }}>
                <Typography variant="caption" color="textSecondary">
                  Already rolled: {option.currency_code} {formatAmount(allocated)}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color={isOverAllocated ? 'error' : 'success.main'}
                >
                  {showAfter ? 'Remaining after allocation' : 'Remaining'}: {remainingCcy} {formatAmount(displayRemaining)}
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => {
        // T3-033 SLICE-ONLY: cap + display against THIS investor's slice of the source (never
        // the whole pool) when the source has a per-investor breakdown.
        const { option: effectiveOption, noSlice } = sliceOptionForInvestor(selectedOption, investorId);
        const serverRemaining = effectiveOption ? getEffectiveRemaining(effectiveOption) : null;
        const currentRolled = parseFloat(rolledFundsAmount) || 0;

        // Single source of truth for the exceed decision — shared with the parent
        // form's rolled-funds field validator via computeRolledExceed().
        const { wouldExceed, remainingConverted, noFx } = computeRolledExceed({
          selectedOption: effectiveOption,
          rolledFundsAmount: currentRolled,
          currencyCode,
          fxRate,
        });
        const afterAllocation = remainingConverted !== null ? remainingConverted - currentRolled : null;

        let computedHelperText = helperText || 'Select the prior contract these funds came from';
        if (selectedOption && noSlice) {
          computedHelperText = `This investor has no rollable balance on ${selectedOption.reference || 'the selected prior contract'}`;
        } else if (selectedOption && serverRemaining !== null) {
          if (isCrossCurrency && fxRate) {
            const convertedServerRemaining = serverRemaining * fxRate;
            computedHelperText = `Remaining after allocation: ${currencyCode} ${formatAmount(convertedServerRemaining - currentRolled)} of ≈ ${currencyCode} ${formatAmount(convertedServerRemaining)} (rate: ${fxPair} ${fxRate.toFixed(4)})`;
          } else if (isCrossCurrency && !fxRate) {
            computedHelperText = `No FX rate for ${selectedOption.currency_code}/${currencyCode} — configure it in Reference Data > FX Rates before rolling these funds`;
          } else {
            const afterAlloc = serverRemaining - currentRolled;
            computedHelperText = `Remaining after allocation: ${selectedOption.currency_code || ''} ${formatAmount(afterAlloc)} of ${formatAmount(selectedOption.capital_amount)}`;
          }
        }

        // A parent can suppress this field's own helper text two ways: pass
        // suppressHelperText (it renders a single full-width line beneath the row itself), or
        // pass error={{ message: '' }} ("flag me red but I'll show the message"). Either way
        // this field shows the red border but no text.
        const errorProvided = error && Object.prototype.hasOwnProperty.call(error, 'message');
        const suppressOwnText = suppressHelperText || (errorProvided && !error.message);
        return (
          <TextField
            {...params}
            label="Prior Contract (Rolled From)"
            error={!!error || wouldExceed || noFx || noSlice}
            helperText={
              suppressOwnText
                ? undefined
                : error?.message
                  ? error.message
                  : noSlice
                    ? computedHelperText
                    : noFx
                      ? computedHelperText
                      : wouldExceed
                        ? `Rolled funds exceed this investor's remaining balance on the prior contract (${currencyCode} ${formatAmount(afterAllocation)})`
                        : computedHelperText
            }
            InputLabelProps={{ shrink: true }}
          />
        );
      }}
    />
    <PriorContractFormDialog
      open={createDialogOpen}
      onClose={() => setCreateDialogOpen(false)}
      onSaved={handleHistoricalSaved}
      investorId={investorId}
      investorName={investorName}
    />
    </>
  );
}

export default PriorContractAutocomplete;
